/**
 * Pure global-diff implementation of in-paragraph layering.
 *
 * Runs a SINGLE diff across the whole paragraph and walks the result,
 * attributing inserts/deletes to markers by adjacency. It does not throw on
 * substantive boundary mismatches; it produces best-effort suggestions.
 *
 * # Why we run the diff TWICE
 *
 * Future readers will notice the algorithm runs two diffs: a global RAW diff
 * for boundary anchoring, then a SEMANTIC diff per marker zone for display.
 * This is deliberate. The two diffs serve incompatible goals:
 *
 *   - Phase 1 (boundary anchoring) needs raw, no-cleanup output. Marker
 *     positions, absorption decisions, and synth-marker placement all depend
 *     on equal segments being preserved exactly where they are. If we used
 *     `diff_cleanupSemantic` here, it would absorb small unchanged stretches
 *     between large edits into surrounding deletes/inserts — blurring marker
 *     boundaries and silently misattributing content.
 *
 *   - Phase 2 (per-zone display) needs semantic cleanup for readability. With
 *     only a raw diff, dense paragraph rewrites that share many words ("the",
 *     "Disclosing", "Information", etc.) come out as interleaved word-level
 *     swaps: `delete word, insert word, delete word, insert word`. That's
 *     correct but unreadable. `diff_cleanupSemantic` consolidates these into
 *     one big delete + one big insert, which is what users actually want to
 *     see.
 *
 * Phase 2's bundling can't blur marker attribution because, by the time it
 * runs, marker boundaries are already locked in by Phase 1. We re-diff only
 * the content WITHIN each zone — never across zone boundaries — so the
 * semantic cleanup has no opportunity to absorb inter-marker context into the
 * wrong marker. Outside-zone equals from Phase 1 stay untouched.
 *
 * The algorithm:
 *   1. Normalize Unicode space variants in `reviewText` (NBSP → ASCII space, etc.).
 *   2. Validate the marker count matches `rangeIds.length`.
 *   3. Build a "stringWithPlaceholders" representation of `existingDiff`:
 *      equals and prior inserts are kept verbatim; prior deletes are replaced
 *      by a single `DELETE_PLACEHOLDER` char per delete (so positions in the
 *      placeholder string align with positions in the original document, and
 *      the diff can find exact alignment).
 *   4. **Phase 1 diff**: `diffWordsRaw` (no semantic cleanup) between the
 *      placeholder string and `reviewText`. Raw mode is critical for boundary
 *      determination.
 *   5. Walk the diff. Restore placeholders to prior-delete segments. Split
 *      marker chars (⟬/⟭) into their own segments.
 *   6. Attribute marker zones: any non-equal segment between a `⟬` and its
 *      matching `⟭` belongs to that marker's id.
 *   7. Absorb adjacent: any non-equal segment OUTSIDE a marker zone but
 *      immediately adjacent to a marker (no equal text between) gets
 *      attributed to that marker's id.
 *   8. Synth-mark mid mismatches: any remaining unattributed non-equal segments
 *      sit between two equal blocks, separated from any marker by equal text.
 *      Bracket the first-to-last such segments in the same paragraph "region"
 *      (between two markers, or marker-to-edge) in a single synthetic marker.
 *   9. Re-attribute prior-insert ranges: equals in the result that overlap a
 *      prior-insert range in the placeholder string become `insert (prior id)`
 *      so prior authorship is preserved when not touched by the new edit.
 *  10. Normalize range markers (move `⟬` before its first content edit and `⟭`
 *      after its last).
 *  11. **Phase 2 diff**: for each marker zone, re-diff its content with
 *      `diffWordsSemantic` and replace the zone's interior with the cleaned-up
 *      result. A prior pending insert the zone leaves alone splits the
 *      interior into runs that are cleaned up separately, so it keeps its own
 *      attribution. A prior insert the zone is rewriting (deleting part of
 *      with its own deletes) joins the re-diff as plain text, and prior
 *      deletes go back at the same place.
 *  12. Merge adjacent same-shape segments. Return.
 */

import { DIFF_DELETE, DIFF_INSERT } from 'diff-match-patch';

import { DELETE_PLACEHOLDER, RANGE_END, RANGE_START } from './constants';
import { normalizeRangeInDiff } from './normalizeRangeInDiff';
import type { DiffSegment } from './types';
import { diffWordsRaw, diffWordsSemantic } from './word-diff';
import { debugLayering } from '../../../debug/loggers';

export const DEFAULT_SYNTHETIC_MARKER_REASONING = 'Adjustment to flow with surrounding changes';

function debugLog(label: string, value: unknown): void {
  if (!debugLayering.enabled) return;
  // Single line per field so the output stays copy-pasteable from the
  // terminal even when individual values are long.
  debugLayering(
    '%s: %s',
    label,
    typeof value === 'string' ? JSON.stringify(value) : JSON.stringify(value)
  );
}

function debugSeparator(label: string): void {
  if (!debugLayering.enabled) return;
  debugLayering('---- layerInParagraphChanges: %s ----', label);
}

export type LayerInParagraphChangesResult = {
  diffs: DiffSegment[];
  syntheticMarkers: Array<{ id: string; reasoning: string }>;
};

export type LayerInParagraphChangesOptions = {
  generateImplicitId?: () => string;
  implicitReasoning?: string;
};

let defaultImplicitIdCounter = 0;
function defaultGenerateImplicitId(): string {
  defaultImplicitIdCounter += 1;
  return `synthetic-${defaultImplicitIdCounter}`;
}

/**
 * Normalizes Unicode "space-like" codepoints to a regular ASCII space, leaving
 * newlines, tabs, and other structural whitespace alone.
 *
 * Aline's editor convention is that paragraph text uses regular ASCII spaces
 * only — never non-breaking spaces or other Unicode space variants. The LLM,
 * however, sometimes emits NBSP (and occasionally other space variants) in its
 * HTML output. Without normalization those would cause mismatches against the
 * visible text, which only ever contains ASCII spaces. The replacement is
 * char-by-char so length is preserved — positions in the normalized string are
 * valid in the original.
 *
 * Codepoints normalized: U+00A0 NO-BREAK SPACE, U+1680 OGHAM SPACE MARK,
 * U+2000–U+200A (en/em/three-per-em quads etc.), U+202F NARROW NO-BREAK SPACE,
 * U+205F MEDIUM MATHEMATICAL SPACE, U+3000 IDEOGRAPHIC SPACE, U+FEFF ZERO WIDTH
 * NO-BREAK SPACE (also the BOM). Newlines, carriage returns, tabs, vertical
 * tabs, and form feeds are intentionally excluded — they carry structural
 * meaning (line breaks, indentation) and must not be flattened to spaces.
 */
function normalizeSpaces(s: string): string {
  return s.replace(/[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000\ufeff]/g, ' ');
}

type PriorEdit =
  | { id: string; kind: 'delete'; placeholderPos: number; text: string }
  | { id: string; kind: 'insert'; rangeEnd: number; rangeStart: number; text: string };

/**
 * Builds a string with one DELETE_PLACEHOLDER char per prior delete (so that
 * positions in the result align with positions in the original document) and a
 * sidecar list of prior edits we'll need to weave back into the final diff.
 */
function buildPlaceholderString(existingDiff: DiffSegment[]): {
  priorEdits: PriorEdit[];
  stringWithPlaceholders: string;
} {
  let s = '';
  const priorEdits: PriorEdit[] = [];
  for (const seg of existingDiff) {
    if (seg.op === 'delete') {
      priorEdits.push({ id: seg.id, kind: 'delete', placeholderPos: s.length, text: seg.text });
      s += DELETE_PLACEHOLDER;
    } else if (seg.op === 'insert') {
      priorEdits.push({
        id: seg.id,
        kind: 'insert',
        rangeEnd: s.length + seg.text.length,
        rangeStart: s.length,
        text: seg.text,
      });
      s += seg.text;
    } else {
      s += seg.text;
    }
  }
  return { priorEdits, stringWithPlaceholders: s };
}

/**
 * An internal diff segment with optional id (for ones we've attributed) and a
 * position range in the placeholder string (for re-attributing equals that
 * overlap prior-insert ranges later).
 */
type WorkingSegment = {
  op: 'delete' | 'equal' | 'insert';
  /**
   * Position range in the placeholder string this segment covers. For equals
   * and deletes (which consume placeholder-string chars), `[start, end)` is
   * the range in placeholder coordinates. For inserts (which DON'T consume
   * placeholder chars), `start === end` and equals the placeholder position
   * at which the insert sits.
   */
  placeholderEnd: number;
  placeholderStart: number;
  text: string;
  /** Optional id for inserts/deletes; equals don't have one. */
  id?: string;
};

/**
 * Converts raw diff tuples to internal working segments. For equal/delete
 * segments (which consume chars from the placeholder string), uses the
 * ORIGINAL chars at those positions — the diff was run on a normalized copy
 * to tolerate Unicode-space differences, but we want the document content
 * unchanged in the output.
 */
function rawDiffsToWorkingSegments(
  rawDiffs: Array<[number, string]>,
  originalStringWithPlaceholders: string
): WorkingSegment[] {
  const result: WorkingSegment[] = [];
  let placeholderPos = 0;
  for (const [op, text] of rawDiffs) {
    if (op === DIFF_INSERT) {
      // Inserts don't consume placeholder-string chars; their text comes from
      // reviewText (which is already normalized).
      result.push({
        op: 'insert',
        placeholderEnd: placeholderPos,
        placeholderStart: placeholderPos,
        text,
      });
    } else if (op === DIFF_DELETE) {
      // Use ORIGINAL chars from the placeholder string at this position,
      // preserving any legacy NBSP / other Unicode whitespace in the document.
      const originalText = originalStringWithPlaceholders.slice(
        placeholderPos,
        placeholderPos + text.length
      );
      result.push({
        op: 'delete',
        placeholderEnd: placeholderPos + text.length,
        placeholderStart: placeholderPos,
        text: originalText,
      });
      placeholderPos += text.length;
    } else {
      // DIFF_EQUAL — same: original chars.
      const originalText = originalStringWithPlaceholders.slice(
        placeholderPos,
        placeholderPos + text.length
      );
      result.push({
        op: 'equal',
        placeholderEnd: placeholderPos + text.length,
        placeholderStart: placeholderPos,
        text: originalText,
      });
      placeholderPos += text.length;
    }
  }
  return result;
}

/**
 * Walks segments and substitutes prior-delete placeholders back as
 * `delete (prior id)` segments. Splits any equal/delete segment that contains
 * a placeholder char.
 */
function restorePlaceholders(
  segments: WorkingSegment[],
  priorDeletes: Array<{ id: string; placeholderPos: number; text: string }>
): WorkingSegment[] {
  if (priorDeletes.length === 0) return segments;

  // Index prior deletes by their placeholder position for O(1) lookup.
  const byPos = new Map<number, { id: string; text: string }>();
  for (const d of priorDeletes) byPos.set(d.placeholderPos, { id: d.id, text: d.text });

  const result: WorkingSegment[] = [];
  for (const seg of segments) {
    if (seg.op === 'insert' || !seg.text.includes(DELETE_PLACEHOLDER)) {
      result.push(seg);
      continue;
    }

    // Split this segment at each placeholder char.
    let cursor = 0;
    let posInPlaceholder = seg.placeholderStart;
    while (cursor < seg.text.length) {
      const phIdx = seg.text.indexOf(DELETE_PLACEHOLDER, cursor);
      if (phIdx === -1) {
        // Remaining text is non-placeholder; emit as same op.
        const text = seg.text.slice(cursor);
        result.push({
          op: seg.op,
          placeholderEnd: posInPlaceholder + text.length,
          placeholderStart: posInPlaceholder,
          text,
        });
        posInPlaceholder += text.length;
        cursor = seg.text.length;
      } else {
        // Emit the run before the placeholder.
        if (phIdx > cursor) {
          const text = seg.text.slice(cursor, phIdx);
          result.push({
            op: seg.op,
            placeholderEnd: posInPlaceholder + text.length,
            placeholderStart: posInPlaceholder,
            text,
          });
          posInPlaceholder += text.length;
        }
        // Emit the placeholder as a prior-delete segment.
        const prior = byPos.get(posInPlaceholder);
        if (prior !== undefined) {
          result.push({
            id: prior.id,
            op: 'delete',
            placeholderEnd: posInPlaceholder + 1,
            placeholderStart: posInPlaceholder,
            text: prior.text,
          });
        }
        // Advance past the placeholder char (1 char in placeholder string).
        posInPlaceholder += 1;
        cursor = phIdx + 1;
      }
    }
  }
  return result;
}

/**
 * Splits any insert segment whose text contains a marker char (⟬ or ⟭) into
 * separate segments — one per marker char and one per content run. After this
 * pass, every marker is its own atomic segment.
 */
function splitMarkersInWorkingSegments(segments: WorkingSegment[]): WorkingSegment[] {
  const result: WorkingSegment[] = [];
  for (const seg of segments) {
    if (seg.op !== 'insert' || (!seg.text.includes(RANGE_START) && !seg.text.includes(RANGE_END))) {
      result.push(seg);
      continue;
    }
    let buffer = '';
    for (const ch of seg.text) {
      if (ch === RANGE_START || ch === RANGE_END) {
        if (buffer.length > 0) {
          result.push({
            op: 'insert',
            placeholderEnd: seg.placeholderStart,
            placeholderStart: seg.placeholderStart,
            text: buffer,
          });
          buffer = '';
        }
        result.push({
          op: 'insert',
          placeholderEnd: seg.placeholderStart,
          placeholderStart: seg.placeholderStart,
          text: ch,
        });
      } else {
        buffer += ch;
      }
    }
    if (buffer.length > 0) {
      result.push({
        op: 'insert',
        placeholderEnd: seg.placeholderStart,
        placeholderStart: seg.placeholderStart,
        text: buffer,
      });
    }
  }
  return result;
}

/**
 * Attributes inserts/deletes that fall inside a marker zone (between ⟬ and ⟭)
 * to that marker's id. Marker chars themselves are also tagged.
 */
function attributeMarkerZones(segments: WorkingSegment[], rangeIds: string[]): WorkingSegment[] {
  let markerIdx = 0;
  let zoneId: string | null = null;
  const result: WorkingSegment[] = [];
  for (const seg of segments) {
    if (seg.op === 'insert' && seg.text === RANGE_START) {
      const id = rangeIds[markerIdx];
      if (id === undefined) {
        throw new Error(
          `layerInParagraphChanges: more RANGE_START markers in diff than rangeIds (markerIdx=${markerIdx})`
        );
      }
      markerIdx += 1;
      zoneId = id;
      result.push({ ...seg, id });
      continue;
    }
    if (seg.op === 'insert' && seg.text === RANGE_END) {
      if (zoneId === null) {
        throw new Error(`layerInParagraphChanges: RANGE_END encountered with no open zone`);
      }
      result.push({ ...seg, id: zoneId });
      zoneId = null;
      continue;
    }
    if (seg.op === 'equal') {
      result.push(seg);
      continue;
    }
    if (zoneId !== null) {
      // We're inside a marker zone — attribute this insert/delete to it,
      // but only if it doesn't already have an id (prior deletes restored
      // from placeholders keep their prior id).
      result.push({ ...seg, id: seg.id ?? zoneId });
    } else {
      result.push(seg);
    }
  }
  return result;
}

/**
 * Absorbs unattributed inserts/deletes that sit immediately adjacent to a
 * marker zone (no equal text between them and the marker). The user agreed to
 * the simplest rule: just look for adjacency, no per-token cap.
 *
 * Specifically:
 *   - For each unattributed non-equal segment, walk left through any
 *     consecutive non-equals/markers to find a marker boundary. If found
 *     before encountering an equal, attribute to that marker.
 *   - Same walking right.
 *   - If both sides have markers without equals between, prefer the left
 *     marker (deterministic tiebreak; chronologically the new edit comes
 *     first in reading order).
 */
function absorbAdjacent(segments: WorkingSegment[]): WorkingSegment[] {
  const result = [...segments];
  for (let i = 0; i < result.length; i++) {
    const seg = result[i]!;
    if (seg.op === 'equal' || seg.id !== undefined) continue;

    // Walk left looking for a marker before an equal.
    let leftId: string | null = null;
    for (let j = i - 1; j >= 0; j--) {
      const prev = result[j]!;
      if (prev.op === 'equal') break;
      if (isMarkerEndSegment(prev)) {
        leftId = prev.id ?? null;
        break;
      }
      // Other unattributed non-equals — keep walking.
    }

    if (leftId !== null) {
      result[i] = { ...seg, id: leftId };
      continue;
    }

    // Walk right looking for a marker before an equal.
    let rightId: string | null = null;
    for (let j = i + 1; j < result.length; j++) {
      const next = result[j]!;
      if (next.op === 'equal') break;
      if (isMarkerStartSegment(next)) {
        rightId = next.id ?? null;
        break;
      }
    }

    if (rightId !== null) {
      result[i] = { ...seg, id: rightId };
    }
  }
  return result;
}

function isMarkerStartSegment(seg: WorkingSegment): boolean {
  return seg.op === 'insert' && seg.text === RANGE_START;
}

function isMarkerEndSegment(seg: WorkingSegment): boolean {
  return seg.op === 'insert' && seg.text === RANGE_END;
}

/**
 * Wraps each "region" of unattributed non-equal segments — i.e., runs that
 * have equal text on both sides anchoring them away from any marker — in a
 * single synthetic marker bracketing first-to-last. Per the user's design:
 * "edge case, handle it simply." Two separate runs in one outer region get
 * one combined synth marker.
 */
function createSynthMarkers(
  segments: WorkingSegment[],
  generateImplicitId: () => string,
  implicitReasoning: string
): { result: WorkingSegment[]; syntheticMarkers: Array<{ id: string; reasoning: string }> } {
  const syntheticMarkers: Array<{ id: string; reasoning: string }> = [];

  // First, identify "regions" — stretches between LLM markers (or between an
  // LLM marker and the paragraph edge) where any unattributed non-equal might
  // exist. Within each region, find first/last unattributed non-equal indices
  // and wrap them in a synth marker.

  type Region = { firstUnattributedIdx: number; lastUnattributedIdx: number };
  const regions: Region[] = [];
  let inRegion = false;
  let regionFirst = -1;
  let regionLast = -1;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    if (isMarkerStartSegment(seg) || isMarkerEndSegment(seg)) {
      // Crossing an LLM marker boundary closes any open region without
      // synthesizing — the unattributed segments in this region got absorbed
      // already via `absorbAdjacent`.
      if (inRegion) {
        regions.push({ firstUnattributedIdx: regionFirst, lastUnattributedIdx: regionLast });
      }
      inRegion = false;
      regionFirst = -1;
      regionLast = -1;
      continue;
    }
    if (seg.op === 'equal') {
      // Equals don't advance region state — they just sit between unattributed
      // non-equals (if any).
      continue;
    }
    if (seg.id !== undefined) {
      // Attributed already (either by zone or by absorb).
      continue;
    }
    // Unattributed non-equal.
    if (!inRegion) {
      inRegion = true;
      regionFirst = i;
    }
    regionLast = i;
  }
  if (inRegion) {
    regions.push({ firstUnattributedIdx: regionFirst, lastUnattributedIdx: regionLast });
  }

  // Build the new segments list, splicing synth markers around each region.
  const result: WorkingSegment[] = [];
  let regionIdx = 0;
  for (let i = 0; i < segments.length; i++) {
    const region = regions[regionIdx];
    if (region !== undefined && i === region.firstUnattributedIdx) {
      const synthId = generateImplicitId();
      syntheticMarkers.push({ id: synthId, reasoning: implicitReasoning });
      // Splice in a synth ⟬ before the first unattributed.
      result.push({
        id: synthId,
        op: 'insert',
        placeholderEnd: segments[i]!.placeholderStart,
        placeholderStart: segments[i]!.placeholderStart,
        text: RANGE_START,
      });
    }
    // Emit the segment, attributing to the synth id if it falls within the region.
    const seg = segments[i]!;
    if (
      region !== undefined &&
      i >= region.firstUnattributedIdx &&
      i <= region.lastUnattributedIdx &&
      seg.op !== 'equal' &&
      seg.id === undefined
    ) {
      const synthId = syntheticMarkers[regionIdx]!.id;
      result.push({ ...seg, id: synthId });
    } else {
      result.push(seg);
    }
    if (region !== undefined && i === region.lastUnattributedIdx) {
      const synthId = syntheticMarkers[regionIdx]!.id;
      // Splice in a synth ⟭ after the last unattributed.
      result.push({
        id: synthId,
        op: 'insert',
        placeholderEnd: segments[i]!.placeholderEnd,
        placeholderStart: segments[i]!.placeholderEnd,
        text: RANGE_END,
      });
      regionIdx += 1;
    }
  }

  return { result, syntheticMarkers };
}

/**
 * Re-attributes equal segments that overlap prior-insert ranges as
 * `insert (prior id)`. This preserves prior authorship for visible content
 * the new edit didn't touch.
 *
 * For each equal segment, check if its placeholder range `[start, end)`
 * overlaps any prior-insert range. If yes, split the equal at the overlap
 * boundaries: portions inside prior-insert ranges become `insert (prior id)`;
 * portions outside stay equal.
 */
function reattributePriorInserts(
  segments: WorkingSegment[],
  priorInserts: Array<{ id: string; rangeEnd: number; rangeStart: number; text: string }>
): WorkingSegment[] {
  if (priorInserts.length === 0) return segments;

  const result: WorkingSegment[] = [];
  for (const seg of segments) {
    if (seg.op !== 'equal') {
      result.push(seg);
      continue;
    }
    // Check overlap with any prior insert range.
    const overlapping = priorInserts.filter(
      p => p.rangeStart < seg.placeholderEnd && p.rangeEnd > seg.placeholderStart
    );
    if (overlapping.length === 0) {
      result.push(seg);
      continue;
    }

    // Walk through the equal segment in placeholder coordinates and emit
    // each chunk attributed appropriately.
    let pos = seg.placeholderStart;
    let textOffset = 0;
    while (pos < seg.placeholderEnd) {
      // Find the prior insert active at `pos`, if any.
      const active = priorInserts.find(p => p.rangeStart <= pos && p.rangeEnd > pos);
      const nextBoundary = active
        ? Math.min(active.rangeEnd, seg.placeholderEnd)
        : Math.min(
            seg.placeholderEnd,
            ...priorInserts.filter(p => p.rangeStart > pos).map(p => p.rangeStart)
          );
      const chunkLen = nextBoundary - pos;
      const chunkText = seg.text.slice(textOffset, textOffset + chunkLen);
      if (active) {
        result.push({
          id: active.id,
          op: 'insert',
          placeholderEnd: pos + chunkLen,
          placeholderStart: pos,
          text: chunkText,
        });
      } else {
        result.push({
          op: 'equal',
          placeholderEnd: pos + chunkLen,
          placeholderStart: pos,
          text: chunkText,
        });
      }
      pos += chunkLen;
      textOffset += chunkLen;
    }
  }
  return result;
}

/**
 * Converts the internal `WorkingSegment[]` to the public `DiffSegment[]`
 * shape (no placeholder-position metadata).
 */
function toDiffSegments(segments: WorkingSegment[]): DiffSegment[] {
  return segments
    .filter(s => s.text.length > 0)
    .map(s => {
      if (s.op === 'equal') return { op: 'equal' as const, text: s.text };
      if (s.id === undefined) {
        throw new Error(
          `layerInParagraphChanges: unattributed ${s.op} segment in final output (text=${JSON.stringify(s.text.slice(0, 30))})`
        );
      }
      if (s.op === 'insert') return { id: s.id, op: 'insert' as const, text: s.text };
      return { id: s.id, op: 'delete' as const, text: s.text };
    });
}

/**
 * Phase 2: re-diff each marker zone's content with
 * `diffWordsSemantic` so the user sees consolidated runs of deletes and
 * inserts instead of interleaved word-level swaps.
 *
 * Why this is a SEPARATE pass from the global diff: see the file-level
 * docstring's "Why we run the diff TWICE" section. Short version: the global
 * diff has to use raw mode (no cleanup) to keep marker boundaries
 * deterministic; semantic cleanup applied globally would absorb unchanged
 * inter-marker context and blur attribution. By the time we reach Phase 2,
 * marker boundaries are already settled, so re-diffing inside each zone is
 * safe — the cleanup never crosses a zone boundary.
 *
 * For each zone:
 *   1. Identify the segments inside (between `⟬` and the matching `⟭`).
 *   2. Split that interior into runs at every segment the re-diff must not
 *      touch (other markers, and prior inserts this zone leaves alone) —
 *      see `consolidateZoneInterior`.
 *   3. For each run, build the OLD text (delete + equal + prior insert
 *      segments concatenated in order) and NEW text (insert + equal + prior
 *      insert segments in order).
 *   4. Run `diffWordsSemantic(old, new)` and convert the result to
 *      DiffSegments tagged with the zone's id. Kept prior-insert text gets
 *      its prior id back, and prior deletes go back at their offsets.
 *   5. Replace the zone's interior with the cleaned-up runs. The markers
 *      themselves, and the segments between runs, are unchanged.
 */
function consolidatePerZone(segments: DiffSegment[], existingDiff: DiffSegment[]): DiffSegment[] {
  const priorIds = new Set(existingDiff.flatMap(seg => (seg.op === 'equal' ? [] : [seg.id])));
  const placed = placeSegments(segments, priorIds);
  const priorInsertSpans = getPriorInsertSpans(existingDiff);

  // Collect marker zones first so we can splice in reverse order without
  // index drift.
  const zones: Array<{ endIdx: number; id: string; startIdx: number }> = [];
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]!;
    if (seg.op !== 'insert' || seg.text !== RANGE_START) continue;
    const id = seg.id;
    let endIdx = -1;
    for (let j = i + 1; j < segments.length; j += 1) {
      const candidate = segments[j]!;
      if (candidate.op === 'insert' && candidate.text === RANGE_END && candidate.id === id) {
        endIdx = j;
        break;
      }
    }
    if (endIdx !== -1) zones.push({ endIdx, id, startIdx: i });
  }

  const result = [...segments];
  for (let z = zones.length - 1; z >= 0; z -= 1) {
    const { endIdx, id, startIdx } = zones[z]!;
    const insideStart = startIdx + 1;
    const insideEnd = endIdx;
    const inside = placed.slice(insideStart, insideEnd);

    // Skip empty zones — nothing to consolidate.
    if (inside.length === 0) continue;

    const consolidated = consolidateZoneInterior({ id, inside, priorIds, priorInsertSpans });
    result.splice(insideStart, insideEnd - insideStart, ...consolidated);
  }

  return result;
}

/** A layered segment with its offset in the paragraph's visible text. */
type PlacedSegment = { seg: DiffSegment; visibleStart: number };

/** Where one author's prior pending insertion sits in the paragraph's visible text. */
type PriorInsertSpan = { end: number; id: string; start: number };

/**
 * Places each layered segment in the paragraph's visible text: the text a
 * reader sees before the new edit, made of equals, prior inserts, and the
 * text the new edit deletes. Prior deletes and new inserts (markers
 * included) take no visible space.
 */
function placeSegments(segments: DiffSegment[], priorIds: ReadonlySet<string>): PlacedSegment[] {
  let offset = 0;
  return segments.map(seg => {
    const visibleStart = offset;
    if (getIsVisibleBeforeEdit(seg, priorIds)) offset += seg.text.length;
    return { seg, visibleStart };
  });
}

function getIsVisibleBeforeEdit(seg: DiffSegment, priorIds: ReadonlySet<string>): boolean {
  if (seg.op === 'equal') return true;
  if (seg.op === 'insert') return priorIds.has(seg.id);
  return !priorIds.has(seg.id);
}

/**
 * Lists the prior pending insertions in visible-text coordinates. Insert
 * nodes of one author that sit directly next to each other form one span:
 * formatting splits one insertion into several nodes, and a reader sees one
 * insertion. Any node between them (a deletion, or an inline element such
 * as a suggestion marker) ends the span. A prior id is the author, not the
 * change, so the nodes on either side may be separate changes, and joining
 * them would let a rewrite of one strike the other.
 */
function getPriorInsertSpans(existingDiff: DiffSegment[]): PriorInsertSpan[] {
  const spans: PriorInsertSpan[] = [];
  let offset = 0;
  let previous: DiffSegment | undefined;
  for (const seg of existingDiff) {
    const start = offset;
    if (seg.op !== 'delete') offset += seg.text.length;
    if (seg.op === 'insert') {
      const last = spans[spans.length - 1];
      const continuesLast = previous?.op === 'insert' && previous.id === seg.id;
      if (last !== undefined && continuesLast) last.end = offset;
      else spans.push({ end: offset, id: seg.id, start });
    }
    previous = seg;
  }
  return spans;
}

/**
 * The prior insertions this zone is rewriting: the ones it deletes part of.
 * Only this zone's own deletes count, so a deletion in another zone never
 * pulls an insertion into this zone's re-diff.
 */
function getRewrittenSpans({
  id,
  inside,
  priorInsertSpans,
}: {
  id: string;
  inside: PlacedSegment[];
  priorInsertSpans: PriorInsertSpan[];
}): PriorInsertSpan[] {
  const ownDeletes = inside.filter(({ seg }) => seg.op === 'delete' && seg.id === id);
  return priorInsertSpans.filter(span =>
    ownDeletes.some(
      ({ seg, visibleStart }) =>
        span.start < visibleStart + seg.text.length && span.end > visibleStart
    )
  );
}

/**
 * Consolidates one zone's interior into runs, each re-diffed on its own.
 *
 * Prior pending edits are already applied in the live paragraph, so the
 * re-diff must leave the live text where it is:
 *   - A prior delete takes no visible space. It joins the run and is put
 *     back at the same visible offset afterwards (see `consolidateRun`).
 *     It must stay a separate segment with its prior id, or
 *     `applyInParagraphEditOutput` would re-apply a deletion already in the
 *     tree.
 *   - A prior insert that this zone deletes part of joins the run as
 *     ordinary visible text, all of it, across every node it spans. The new
 *     edit is rewriting it, and the words the two versions share ("the",
 *     "for", the spaces) are coincidence. Kept as walls, those shared words
 *     chopped the zone into one-word runs and the user saw `delete word,
 *     insert word` for the whole rewrite. Whatever the re-diff keeps gets
 *     the prior id back.
 *   - A prior insert this zone leaves alone ends the run, so its author
 *     keeps it even when the rewrite around it is large.
 *
 * Marker chars and another new edit's segments also end the run.
 *
 * Every run preserves its OLD text (deletes + equals + prior inserts) and
 * NEW text (inserts + equals + prior inserts), so the zone's text on both
 * sides is unchanged and marker offsets downstream still line up.
 */
function consolidateZoneInterior({
  id,
  inside,
  priorIds,
  priorInsertSpans,
}: {
  id: string;
  inside: PlacedSegment[];
  priorIds: ReadonlySet<string>;
  priorInsertSpans: PriorInsertSpan[];
}): DiffSegment[] {
  const rewrittenSpans = getRewrittenSpans({ id, inside, priorInsertSpans });
  let run: PlacedSegment[] = [];
  const output: DiffSegment[] = [];

  const flushRun = () => {
    output.push(...consolidateRun({ id, priorIds, priorInsertSpans, run }));
    run = [];
  };

  for (const placedSeg of inside) {
    if (getJoinsRun({ id, placedSeg, priorIds, rewrittenSpans })) {
      run.push(placedSeg);
      continue;
    }
    flushRun();
    output.push(placedSeg.seg);
  }
  flushRun();

  return output;
}

function getJoinsRun({
  id,
  placedSeg,
  priorIds,
  rewrittenSpans,
}: {
  id: string;
  placedSeg: PlacedSegment;
  priorIds: ReadonlySet<string>;
  rewrittenSpans: PriorInsertSpan[];
}): boolean {
  const { seg, visibleStart } = placedSeg;
  if (seg.text === RANGE_START || seg.text === RANGE_END) return false;
  if (seg.op === 'equal' || seg.id === id) return true;
  if (!priorIds.has(seg.id)) return false;
  if (seg.op === 'delete') return true;
  return rewrittenSpans.some(span => span.start <= visibleStart && visibleStart < span.end);
}

function consolidateRun({
  id,
  priorIds,
  priorInsertSpans,
  run,
}: {
  id: string;
  priorIds: ReadonlySet<string>;
  priorInsertSpans: PriorInsertSpan[];
  run: PlacedSegment[];
}): DiffSegment[] {
  const first = run[0];
  if (first === undefined) return [];

  /**
   * OLD text uses the same visibility rule as `placeSegments`, so offsets
   * into it line up with the visible offsets the spans use. NEW text is
   * everything the edit leaves visible: all but deletions.
   */
  let oldText = '';
  let newText = '';
  const priorDeletes: { offset: number; seg: DiffSegment }[] = [];
  for (const { seg } of run) {
    if (getIsVisibleBeforeEdit(seg, priorIds)) oldText += seg.text;
    if (seg.op !== 'delete') newText += seg.text;
    else if (priorIds.has(seg.id)) priorDeletes.push({ offset: oldText.length, seg });
  }

  /**
   * Nothing to diff against (e.g. a pure-insert marker with no old content
   * on either side) — the existing segments already represent the right
   * thing.
   */
  if (oldText.length === 0 && newText.length === 0) return run.map(({ seg }) => seg);

  const output: DiffSegment[] = [];
  /**
   * A prior delete goes back at its visible offset, ahead of anything that
   * starts there, so it reads as part of the struck text before the new
   * text.
   */
  const emitPriorDeletesAt = (offset: number) => {
    let next = priorDeletes[0];
    while (next !== undefined && next.offset <= offset) {
      output.push(next.seg);
      priorDeletes.shift();
      next = priorDeletes[0];
    }
  };

  let oldPos = 0;
  for (const [op, text] of diffWordsSemantic(oldText, newText)) {
    if (op === DIFF_INSERT) {
      emitPriorDeletesAt(oldPos);
      output.push({ id, op: 'insert', text });
      continue;
    }
    const end = oldPos + text.length;
    while (oldPos < end) {
      emitPriorDeletesAt(oldPos);
      const nextPriorDeleteOffset = priorDeletes[0]?.offset ?? end;
      if (op === DIFF_DELETE) {
        const chunkEnd = Math.min(end, nextPriorDeleteOffset);
        output.push({ id, op: 'delete', text: oldText.slice(oldPos, chunkEnd) });
        oldPos = chunkEnd;
        continue;
      }
      // Kept text goes back to whoever owns it: original text or a prior insert.
      const owner = getVisibleOwner(priorInsertSpans, first.visibleStart + oldPos);
      const chunkEnd = Math.min(end, nextPriorDeleteOffset, owner.end - first.visibleStart);
      const chunkText = oldText.slice(oldPos, chunkEnd);
      output.push(
        owner.id === undefined
          ? { op: 'equal', text: chunkText }
          : { id: owner.id, op: 'insert', text: chunkText }
      );
      oldPos = chunkEnd;
    }
  }
  emitPriorDeletesAt(Infinity);

  return output;
}

/**
 * Returns the prior insert that owns the visible char at `offset` (none for
 * original text) and the visible offset where that ownership ends.
 */
function getVisibleOwner(
  priorInsertSpans: PriorInsertSpan[],
  offset: number
): { end: number; id: string | undefined } {
  const span = priorInsertSpans.find(s => s.start <= offset && offset < s.end);
  if (span !== undefined) return { end: span.end, id: span.id };
  const nextStarts = priorInsertSpans.filter(s => s.start > offset).map(s => s.start);
  return { end: Math.min(Infinity, ...nextStarts), id: undefined };
}

/**
 * Merges adjacent same-shape segments. Specifically:
 *   - Adjacent equals merge.
 *   - Adjacent same-id inserts merge — UNLESS either is a marker char
 *     (RANGE_START / RANGE_END), which must stay atomic so downstream
 *     extraction can identify them.
 *   - Adjacent same-id deletes merge.
 *
 * The marker-char carve-out is the same one the rest of the pipeline relies
 * on — single-char marker inserts must remain identifiable.
 */
function mergeAdjacentSegments(changes: DiffSegment[]): DiffSegment[] {
  const result: DiffSegment[] = [];
  for (const change of changes) {
    if (change.text.length === 0) continue;
    const last = result[result.length - 1];

    if (last !== undefined && last.op === change.op) {
      if (change.op === 'equal') {
        last.text += change.text;
        continue;
      }
      // Insert or delete — must share the same id.
      if (last.op !== 'equal' && (change.op === 'insert' || change.op === 'delete')) {
        const lastIsMarker =
          last.op === 'insert' && (last.text === RANGE_START || last.text === RANGE_END);
        const changeIsMarker =
          change.op === 'insert' && (change.text === RANGE_START || change.text === RANGE_END);
        if (!lastIsMarker && !changeIsMarker && last.id === change.id) {
          last.text += change.text;
          continue;
        }
      }
    }

    if (change.op === 'equal') result.push({ op: 'equal', text: change.text });
    else if (change.op === 'insert')
      result.push({ id: change.id, op: 'insert', text: change.text });
    else result.push({ id: change.id, op: 'delete', text: change.text });
  }
  return result;
}

/**
 * Layers an in-paragraph LLM edit onto a paragraph's existing diff.
 *
 * @param existingDiff - The paragraph's current state expressed as
 *   `DiffSegment[]` (prior pending edits are preserved as-is in the output).
 * @param reviewText - The full paragraph text from the LLM with
 *   `RANGE_START`/`RANGE_END` pairs around each region the LLM intends to
 *   change.
 * @param rangeIds - The id to attribute to each marked region, in document
 *   order (one entry per `RANGE_START`/`RANGE_END` pair).
 * @param options - `generateImplicitId` supplies ids for synthetic markers
 *   (changes the diff finds outside any marked region); `implicitReasoning`
 *   sets their reasoning string.
 *
 * Runs a single global diff across the whole paragraph and walks the result,
 * attributing inserts/deletes to markers by adjacency and synthesizing
 * markers for non-adjacent changes. Produces a best-effort result rather than
 * throwing on substantive boundary mismatches.
 *
 * @returns `diffs` — the layered `DiffSegment[]` — and `syntheticMarkers`,
 *   the markers that were synthesized for changes outside any marked region.
 */
export function layerInParagraphChanges(
  existingDiff: DiffSegment[],
  reviewText: string,
  rangeIds: string[],
  options: LayerInParagraphChangesOptions = {}
): LayerInParagraphChangesResult {
  const generateImplicitId = options.generateImplicitId ?? defaultGenerateImplicitId;
  const implicitReasoning = options.implicitReasoning ?? DEFAULT_SYNTHETIC_MARKER_REASONING;

  debugSeparator('begin');
  debugLog('reviewText (raw)', reviewText);
  debugLog('rangeIds', rangeIds);
  debugLog('existingDiff (paragraph state BEFORE)', existingDiff);

  // 1. Normalize Unicode space variants in reviewText.
  const normalizedReviewText = normalizeSpaces(reviewText);
  debugLog('reviewText (after space normalization)', normalizedReviewText);

  // 2. Validate marker count.
  const markerStartCount = countOccurrences(normalizedReviewText, RANGE_START);
  if (markerStartCount !== rangeIds.length) {
    throw new Error(
      `layerInParagraphChanges: rangeIds length (${rangeIds.length}) does not match marker pair count (${markerStartCount})`
    );
  }

  // 3. Build placeholder string.
  const { priorEdits, stringWithPlaceholders } = buildPlaceholderString(existingDiff);
  const priorDeletes = priorEdits.filter(
    (p): p is Extract<PriorEdit, { kind: 'delete' }> => p.kind === 'delete'
  );
  const priorInserts = priorEdits.filter(
    (p): p is Extract<PriorEdit, { kind: 'insert' }> => p.kind === 'insert'
  );
  debugLog('stringWithPlaceholders', stringWithPlaceholders);
  debugLog('priorEdits', priorEdits);

  // 4. Run raw word diff (no semantic cleanup) against normalized placeholder
  //    string so legacy Unicode-space variants in the document don't trip up
  //    matching. Output equals/deletes use the ORIGINAL chars (see
  //    rawDiffsToWorkingSegments).
  const normalizedStringWithPlaceholders = normalizeSpaces(stringWithPlaceholders);
  const rawDiffs = diffWordsRaw(normalizedStringWithPlaceholders, normalizedReviewText);
  debugLog('rawDiffs (Phase 1 diffWordsRaw output)', rawDiffs);

  // 5. Convert to internal format and process placeholders + markers.
  let segments = rawDiffsToWorkingSegments(rawDiffs, stringWithPlaceholders);
  debugLog('after rawDiffsToWorkingSegments', segments);
  segments = restorePlaceholders(segments, priorDeletes);
  debugLog('after restorePlaceholders', segments);
  segments = splitMarkersInWorkingSegments(segments);
  debugLog('after splitMarkersInWorkingSegments', segments);

  // 6. Attribute marker zones.
  segments = attributeMarkerZones(segments, rangeIds);
  debugLog('after attributeMarkerZones', segments);

  // 7. Absorb adjacent unattributed non-equals into the nearest marker.
  segments = absorbAdjacent(segments);
  debugLog('after absorbAdjacent', segments);

  // 8. Synth-mark mid mismatches.
  const { result: withSynth, syntheticMarkers } = createSynthMarkers(
    segments,
    generateImplicitId,
    implicitReasoning
  );
  debugLog('after createSynthMarkers', withSynth);
  debugLog('syntheticMarkers', syntheticMarkers);

  // 9. Re-attribute prior-insert ranges (equals overlapping prior-insert
  //    ranges become `insert (prior id)`).
  const reattributed = reattributePriorInserts(withSynth, priorInserts);
  debugLog('after reattributePriorInserts', reattributed);

  // 10. Convert to public shape and normalize range markers.
  const asDiffSegments = toDiffSegments(reattributed);
  debugLog('after toDiffSegments', asDiffSegments);
  const normalized = normalizeRangeInDiff(asDiffSegments);
  debugLog('after normalizeRangeInDiff', normalized);

  // 11. Phase 2: re-diff each marker zone's content with `diffWordsSemantic`
  //     to consolidate interleaved word swaps into clean runs of deletes
  //     followed by inserts. See `consolidatePerZone`'s docstring and the
  //     file-level "Why we run the diff TWICE" section for why this is a
  //     separate pass from the Phase 1 global diff.
  const consolidated = consolidatePerZone(normalized, existingDiff);
  debugLog('after consolidatePerZone (Phase 2)', consolidated);

  // 12. Merge adjacent same-shape segments.
  const merged = mergeAdjacentSegments(consolidated);
  debugLog('after mergeAdjacentSegments (FINAL)', merged);
  debugSeparator('end');

  return { diffs: merged, syntheticMarkers };
}

function countOccurrences(s: string, ch: string): number {
  let count = 0;
  for (const c of s) if (c === ch) count += 1;
  return count;
}
